"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import {
  Search,
  Copy,
  Check,
  ExternalLink,
  Layers,
  Terminal,
  Grid3X3,
  LayoutGrid,
} from "lucide-react";

interface IconLib {
  name: string;
  url: string;
  image: string;
  packageName: string;
  iconsCount: string;
  category: "popular" | "large" | "minimal" | "all";
  tags: string[];
  description: string;
}

const ICON_LIBS: IconLib[] = [
  {
    name: "Lucide React",
    url: "https://lucide.dev/",
    image: "https://lucide.dev/og.png",
    packageName: "lucide-react",
    iconsCount: "1.500+ Icons",
    category: "popular",
    tags: ["Standard", "shadcn/ui", "Feather Fork"],
    description:
      "Der De-facto-Standard für moderne React- und Next.js-Projekte (z. B. in shadcn/ui). Bietet über 1.500 konsistente Vektorsymbole mit anpassbarer Strichstärke.",
  },
  {
    name: "React Icons",
    url: "https://react-icons.github.io/react-icons/",
    image: "https://opengraph.githubassets.com/1/react-icons/react-icons",
    packageName: "react-icons",
    iconsCount: "100.000+ Icons",
    category: "large",
    tags: ["Meta-Library", "Tree-Shaking", "All-in-One"],
    description:
      "Die populärste Meta-Library für React. Bündelt Dutzende Icon-Sets (Font Awesome, Material, Ant, Tabler etc.) mit Tree-Shaking für minimale Bundle-Größen.",
  },
  {
    name: "Heroicons",
    url: "https://heroicons.com/",
    image: "https://heroicons.com/_next/static/media/social-card.fefc68e0.jpg",
    packageName: "@heroicons/react",
    iconsCount: "300+ Icons",
    category: "popular",
    tags: ["Tailwind CSS", "Outline & Solid", "Micro/Mini"],
    description:
      "Vom Tailwind CSS-Kernteam entworfen. Perfekt auf moderne Tailwind-Designs abgestimmt mit handgefertigten Varianten für Outline, Solid und Mini-Raster.",
  },
  {
    name: "Tabler Icons",
    url: "https://tabler-icons.io/",
    image: "https://tabler.io/icons/opengraph-image-1v9qf3.png?opengraph-image.20zbtqc-elllk.png",
    packageName: "@tabler/icons-react",
    iconsCount: "5.500+ Icons",
    category: "large",
    tags: ["Open Source", "Konfigurierbar", "Stroked"],
    description:
      "Riesige Open-Source-Sammlung von über 5.500 anpassbaren Vektor-Icons. Liefert flexible SVG-Komponenten mit anpassbarer Konturstärke und Farbe.",
  },
  {
    name: "Phosphor Icons",
    url: "https://phosphoricons.com/",
    image: "https://phosphoricons.com/phosphor-opengraph.png?v=2.0.2",
    packageName: "@phosphor-icons/react",
    iconsCount: "9.000+ Icons",
    category: "popular",
    tags: ["6 Gewichte", "Duotone", "Harmonisch"],
    description:
      "Ein hochflexibles, elegantes Icon-System mit über 9.000 Icons in 6 Strichstärken (Thin bis Fill) für konsistente Benutzeroberflächen.",
  },
  {
    name: "Hugeicons",
    url: "https://hugeicons.com/",
    image: "https://hugeicons.com/og_preview.png",
    packageName: "hugeicons-react",
    iconsCount: "60.000+ Icons",
    category: "large",
    tags: ["SaaS & Dashboards", "10 Stile", "Lucide Migration"],
    description:
      "Umfassende Icon-Bibliothek für SaaS und Dashboards mit über 60.000 Icons in 10 Stilen und offiziellem Migrationspfad von Lucide.",
  },
  {
    name: "Iconify",
    url: "https://icon-sets.iconify.design/",
    image: "https://icon-sets.iconify.design/og-image.png",
    packageName: "@iconify/react",
    iconsCount: "200.000+ Icons",
    category: "large",
    tags: ["Universell", "On-Demand", "150+ Sets"],
    description:
      "Ein universelles Icon-Framework mit Zugriff auf über 200.000 Icons aus 150+ Sets. Lädt Icons bei Bedarf on-demand nach.",
  },
  {
    name: "Radix UI Icons",
    url: "https://icons.radix-ui.com/",
    image: "https://radix-ui.com/icons/opengraph-image.png?opengraph-image.3ksnf9bqqwt8y.png",
    packageName: "@radix-ui/react-icons",
    iconsCount: "300+ Icons",
    category: "minimal",
    tags: ["15x15 Grid", "Minimalistisch", "Design Systems"],
    description:
      "Ein Set von 15x15 Icons speziell für Radix Primitives und Design-Systeme. Extrem präzise, schnörkellos und optimal für kompakte Bedienelemente.",
  },
  {
    name: "Font Awesome",
    url: "https://fontawesome.com/docs/web/use-with/react/",
    image: "https://img.fortawesome.com/1ce05b4b/open-graph-general.png",
    packageName: "@fortawesome/react-fontawesome",
    iconsCount: "30.000+ Icons",
    category: "large",
    tags: ["Klassiker", "Brands", "Multi-Style"],
    description:
      "Der Klassiker unter den Icon-Sets. Bietet tausende Icons in verschiedenen Stilen (Solid, Regular, Duotone) plus eine riesige Markensammlung.",
  },
  {
    name: "Material UI Icons",
    url: "https://mui.com/material-ui/material-icons/",
    image: "https://mui.com/edge-functions/og-image?product=Material%20UI&title=Material%20Icons&description=2%2C100%2B%20ready-to-use%20React%20Material%20Icons%20from%20the%20official%20website.",
    packageName: "@mui/icons-material",
    iconsCount: "2.100+ Icons",
    category: "popular",
    tags: ["Google Material", "MUI", "5 Varianten"],
    description:
      "Die offizielle Umsetzung von Googles Material Design Icons als React-Komponenten. Ideal für Projekte mit Material-UI-Design.",
  },
];

export default function Detail() {
  const [search, setSearch] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [copiedPackage, setCopiedPackage] = useState<string | null>(null);
  const [density, setDensity] = useState<"compact" | "normal">("compact");

  const filteredLibs = useMemo(() => {
    return ICON_LIBS.filter((lib) => {
      const matchesFilter =
        selectedFilter === "all" ||
        (selectedFilter === "popular" && lib.category === "popular") ||
        (selectedFilter === "large" && lib.category === "large") ||
        (selectedFilter === "minimal" && lib.category === "minimal");

      if (!matchesFilter) return false;

      if (!search.trim()) return true;

      const q = search.toLowerCase();
      return (
        lib.name.toLowerCase().includes(q) ||
        lib.description.toLowerCase().includes(q) ||
        lib.packageName.toLowerCase().includes(q) ||
        lib.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [search, selectedFilter]);

  const copyCommand = (packageName: string) => {
    const cmd = `pnpm add ${packageName}`;
    navigator.clipboard.writeText(cmd);
    setCopiedPackage(packageName);
    setTimeout(() => {
      setCopiedPackage((current) => (current === packageName ? null : current));
    }, 2000);
  };

  return (
    <div className="w-full space-y-8">
      {/* ─── Filter, Search & View Cockpit ─── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-2xl border border-neutral-200/80 bg-neutral-50/60 p-3 sm:p-4 dark:border-neutral-800/80 dark:bg-neutral-900/40">
        {/* Search input */}
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen (z. B. lucide, tailwind)..."
            className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-10 pr-3.5 text-xs sm:text-sm text-neutral-900 shadow-2xs transition-all placeholder:text-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white dark:placeholder:text-neutral-500 dark:focus:border-neutral-400"
          />
        </div>

        {/* Category Pills & Density Switch */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 sm:justify-end">
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: "all", label: "Alle", count: ICON_LIBS.length },
              { id: "popular", label: "Populär", count: ICON_LIBS.filter((l) => l.category === "popular").length },
              { id: "large", label: "Große Sets", count: ICON_LIBS.filter((l) => l.category === "large").length },
              { id: "minimal", label: "Kompakt", count: ICON_LIBS.filter((l) => l.category === "minimal").length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedFilter(tab.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                  selectedFilter === tab.id
                    ? "bg-neutral-900 text-white shadow-xs dark:bg-white dark:text-neutral-900"
                    : "border border-neutral-200/80 bg-white/80 text-neutral-600 hover:border-neutral-300 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/80 dark:text-neutral-400 dark:hover:border-neutral-700 dark:hover:text-white"
                }`}
              >
                {tab.label}
                <span className="ml-1.5 opacity-60 font-mono text-[10px]">{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Density toggle buttons */}
          <div className="hidden sm:flex items-center gap-1 rounded-full border border-neutral-200 bg-white p-1 dark:border-neutral-800 dark:bg-neutral-950">
            <button
              onClick={() => setDensity("compact")}
              title="Kompakte Ansicht (4 Spalten)"
              className={`rounded-full p-1.5 transition-all ${
                density === "compact"
                  ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                  : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              <Grid3X3 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setDensity("normal")}
              title="Standard Ansicht (3 Spalten)"
              className={`rounded-full p-1.5 transition-all ${
                density === "normal"
                  ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                  : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ─── Grid of Icon Libraries ─── */}
      {filteredLibs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-300 py-16 text-center dark:border-neutral-800">
          <Layers className="h-8 w-8 text-neutral-400 mb-2 opacity-60" />
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Keine Icon-Bibliotheken für &quot;{search}&quot; gefunden.
          </p>
          <button
            onClick={() => {
              setSearch("");
              setSelectedFilter("all");
            }}
            className="mt-2 text-xs font-medium text-blue-600 underline hover:text-blue-500 dark:text-blue-400"
          >
            Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div
          className={`grid gap-5 ${
            density === "compact"
              ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {filteredLibs.map((lib, idx) => {
            const isCopied = copiedPackage === lib.packageName;

            return (
              <div
                key={lib.name}
                className="group relative flex flex-col overflow-hidden rounded-xl border border-black/10 bg-white/90 shadow-xs backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-black/25 hover:shadow-lg dark:border-white/10 dark:bg-neutral-900/80 dark:hover:border-white/25"
              >
                {/* ─── Image Container with Aspect Ratio ─── */}
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                  <Image
                    src={lib.image}
                    alt={`${lib.name} Vorschau`}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1536px) 33vw, 25vw"
                    priority={idx < 4}
                    className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />
                  {/* Subtle Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />

                  {/* Top Stats & Links overlay */}
                  <div className="absolute inset-x-2.5 top-2.5 flex items-center justify-between">
                    <span className="rounded-full border border-white/20 bg-black/60 px-2 py-0.5 font-mono text-[10px] font-semibold text-white backdrop-blur-md">
                      {lib.iconsCount}
                    </span>

                    <a
                      href={lib.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${lib.name} Website öffnen`}
                      aria-label={`${lib.name} Website in neuem Tab öffnen`}
                      className="flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur-md transition-all hover:scale-110 hover:bg-black/80"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>

                {/* ─── Card Content ─── */}
                <div className="flex flex-1 flex-col justify-between p-3.5">
                  <div>
                    {/* Header: Title */}
                    <div className="flex items-start justify-between gap-1.5">
                      <a
                        href={lib.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-heading inline-flex items-center gap-1 text-sm font-bold tracking-tight text-neutral-900 transition-colors hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
                      >
                        {lib.name}
                      </a>
                    </div>

                    {/* Tags */}
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {lib.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded border border-neutral-200/70 bg-neutral-100/70 px-1.5 py-0.2 text-[10px] font-medium text-neutral-600 dark:border-neutral-800 dark:bg-neutral-800/70 dark:text-neutral-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Description - clamped to 2 lines for compact height */}
                    <p className="mt-2 text-xs leading-relaxed text-neutral-600 dark:text-neutral-300 line-clamp-2">
                      {lib.description}
                    </p>
                  </div>

                  {/* Footer: Package & Install command */}
                  <div className="mt-3 border-t border-neutral-100 pt-2.5 dark:border-neutral-800/80">
                    <div className="flex items-center justify-between gap-1.5">
                      <div className="flex items-center gap-1 font-mono text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                        <Terminal className="h-3 w-3 shrink-0 opacity-70" />
                        <span className="truncate">pnpm add {lib.packageName}</span>
                      </div>

                      <button
                        onClick={() => copyCommand(lib.packageName)}
                        className={`shrink-0 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium transition-all ${
                          isCopied
                            ? "border-emerald-500/50 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400"
                            : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:border-neutral-300 hover:bg-white dark:border-neutral-800 dark:bg-neutral-800/50 dark:text-neutral-300 dark:hover:border-neutral-700 dark:hover:bg-neutral-800"
                        }`}
                        title="Befehl kopieren"
                      >
                        {isCopied ? (
                          <>
                            <Check className="h-3 w-3 text-emerald-500" />
                            <span>Kopiert</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 opacity-70" />
                            <span>Kopieren</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
